import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import InteractiveAudioPlayer from "@/components/InteractiveAudioPlayer";
import { 
  PhoneIncoming, 
  PhoneOutgoing, 
  TrendingUp, 
  Headphones,
  CheckCircle2
} from "lucide-react";
import logo from "@/assets/holionix-logo.jpeg";
import inboundHero from "@/assets/inbound-support-hero-v2.jpg";
import outboundSalesHero from "@/assets/outbound-sales-hero-v2.jpg";
import marketingHero from "@/assets/marketing-campaigns-hero-v2.jpg";
import supportDeskHero from "@/assets/support-desk-hero-v2.jpg";
import { useTranslation } from 'react-i18next';

const Solutions = () => {
  const { t } = useTranslation();
  const useCases = {
    inbound: {
      icon: PhoneIncoming,
      title: t('solutions.inbound_title'),
      subtitle: t('solutions.inbound_subtitle'),
      image: inboundHero,
      challenges: [
        t('solutions.inbound_challenge_1'),
        t('solutions.inbound_challenge_2'),
        t('solutions.inbound_challenge_3'),
        t('solutions.inbound_challenge_4')
      ],
      solutions: [
        t('solutions.inbound_solution_1'),
        t('solutions.inbound_solution_2'),
        t('solutions.inbound_solution_3'),
        t('solutions.inbound_solution_4')
      ],
      impact: [
        { metric: "40%", label: t('solutions.inbound_impact_1') },
        { metric: "100%", label: t('solutions.inbound_impact_2') },
        { metric: "90%", label: t('solutions.inbound_impact_3') },
        { metric: "35%", label: t('solutions.inbound_impact_4') }
      ]
    },
    outboundSales: {
      icon: PhoneOutgoing,
      title: t('solutions.outbound_title'),
      subtitle: t('solutions.outbound_subtitle'),
      image: outboundSalesHero,
      challenges: [
        t('solutions.outbound_challenge_1'),
        t('solutions.outbound_challenge_2'),
        t('solutions.outbound_challenge_3'),
        t('solutions.outbound_challenge_4')
      ],
      solutions: [
        t('solutions.outbound_solution_1'),
        t('solutions.outbound_solution_2'),
        t('solutions.outbound_solution_3'),
        t('solutions.outbound_solution_4')
      ],
      impact: [
        { metric: "3x", label: t('solutions.outbound_impact_1') },
        { metric: "60%", label: t('solutions.outbound_impact_2') },
        { metric: "5x", label: t('solutions.outbound_impact_3') },
        { metric: "50%", label: t('solutions.outbound_impact_4') }
      ]
    },
    marketing: {
      icon: TrendingUp,
      title: t('solutions.marketing_title'),
      subtitle: t('solutions.marketing_subtitle'),
      image: marketingHero,
      challenges: [
        t('solutions.marketing_challenge_1'),
        t('solutions.marketing_challenge_2'),
        t('solutions.marketing_challenge_3'),
        t('solutions.marketing_challenge_4')
      ],
      solutions: [
        t('solutions.marketing_solution_1'),
        t('solutions.marketing_solution_2'),
        t('solutions.marketing_solution_3'),
        t('solutions.marketing_solution_4')
      ],
      impact: [
        { metric: "4x", label: t('solutions.marketing_impact_1') },
        { metric: "70%", label: t('solutions.marketing_impact_2') },
        { metric: "100%", label: t('solutions.marketing_impact_3') },
        { metric: "2x", label: t('solutions.marketing_impact_4') }
      ]
    },
    supportDesk: {
      icon: Headphones,
      title: t('solutions.support_title'),
      subtitle: t('solutions.support_subtitle'),
      image: supportDeskHero,
      challenges: [
        t('solutions.support_challenge_1'),
        t('solutions.support_challenge_2'),
        t('solutions.support_challenge_3'),
        t('solutions.support_challenge_4')
      ],
      solutions: [
        t('solutions.support_solution_1'),
        t('solutions.support_solution_2'),
        t('solutions.support_solution_3'),
        t('solutions.support_solution_4')
      ],
      impact: [
        { metric: "80%", label: t('solutions.support_impact_1') },
        { metric: "60%", label: t('solutions.support_impact_2') },
        { metric: "45%", label: t('solutions.support_impact_3') },
        { metric: "90%", label: t('solutions.support_impact_4') }
      ]
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-shadow-glow">
              {t('solutions.title')}
            </h1>
            <p className="text-xl text-foreground/80 max-w-4xl mx-auto">
              {t('solutions.subtitle')}
            </p>
          </motion.div>

          <Tabs defaultValue="inbound" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12 h-auto glass-effect">
              {Object.entries(useCases).map(([key, useCase]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="flex items-center gap-2 py-4 data-[state=active]:gradient-border"
                >
                  <useCase.icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{useCase.title.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(useCases).map(([key, useCase]) => (
              <TabsContent key={key} value={key} className="space-y-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative rounded-2xl overflow-hidden mb-12"
                >
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent flex items-end p-8">
                    <div className="text-center w-full">
                      <div className="w-20 h-20 rounded-2xl gradient-brand mx-auto mb-6 flex items-center justify-center glow-effect">
                        <useCase.icon className="w-10 h-10 text-white" />
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-4">{useCase.title}</h2>
                      <p className="text-xl text-accent font-semibold">{useCase.subtitle}</p>
                    </div>
                  </div>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12">
                  <Card className="p-8 card-dark border-destructive/30">
                    <h3 className="text-2xl font-bold mb-6 text-destructive">{t('solutions.challenges')}</h3>
                    <ul className="space-y-4">
                      {useCase.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-foreground/70">
                          <span className="text-destructive">•</span> {challenge}
                        </li>
                      ))}
                    </ul>
                  </Card>

                  <Card className="p-8 card-dark gradient-border glow-effect">
                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-accent" /> {t('solutions.solution')}
                    </h3>
                    <ul className="space-y-4">
                      {useCase.solutions.map((solution, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <span className="font-medium">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </div>

                <Card className="p-10 card-dark">
                  <h3 className="text-3xl font-bold mb-8 text-center">{t('solutions.results')}</h3>
                  <div className="grid md:grid-cols-4 gap-8">
                    {useCase.impact.map((item, idx) => (
                      <motion.div 
                        key={idx} 
                        className="text-center"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="text-5xl font-bold gradient-brand-text mb-2 text-shadow-glow">{item.metric}</div>
                        <div className="text-sm text-foreground/60">{item.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </Card>

                <div className="max-w-xl mx-auto">
                  <h3 className="text-2xl font-bold mb-6 text-center">{t('solutions.hear_action')}</h3>
                  <div className="card-dark p-6 rounded-xl">
                    <InteractiveAudioPlayer
                      agentName={useCase.title}
                      agentRole={useCase.subtitle}
                      avatar={logo}
                      accent="American English"
                      audioType={key === 'inbound' ? 'inbound-support' : key === 'outboundSales' ? 'outbound-sales' : key === 'marketing' ? 'marketing' : 'support-desk'}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/contact">
                    <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-7 glow-effect">
                      {t('solutions.cta_demo')}
                    </Button>
                  </Link>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Solutions;
